import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search as SearchIcon, X, Loader2 } from "lucide-react";
import { api, formatPrice, bridgeImage } from "../lib/api";
import { useLanguage } from "../lib/language";
import { normalizeText, searchProducts } from "../lib/search";
import { SmartImg } from "./SmartImg";

// Module-level cache of the full bridge inventory. Shared across every mount
// of SearchBar so navigating between pages doesn't re-fetch 647 products.
let _invCache = null;
let _invCachedAt = 0;
const INV_TTL_MS = 2 * 60 * 1000; // 2 minutes

async function getInventoryCached() {
    const now = Date.now();
    if (_invCache && (now - _invCachedAt) < INV_TTL_MS) return _invCache;
    const { data } = await api.get("/bridge/inventario");
    _invCache = data?.products || [];
    _invCachedAt = now;
    return _invCache;
}

// Persistent, always-visible search bar rendered on every page except /checkout.
// Reads products in-memory from the bridge inventory cache and filters locally,
// so results appear instantly as the user types.
export default function SearchBar() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [query, setQuery] = useState("");
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const path = location.pathname;
    const visible = path === "/" || path === "/tienda";

    // Sync the input from the ?q= URL param whenever we're on the shop page.
    useEffect(() => {
        if (path === "/tienda") {
            const urlQ = new URLSearchParams(location.search).get("q") || "";
            setQuery((prev) => (prev === urlQ ? prev : urlQ));
        }
    }, [path, location.search]);

    // Close the dropdown on route changes.
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Load (and cache) the bridge inventory on first visible render.
    useEffect(() => {
        if (!visible) return;
        if (inventory.length > 0) return;
        setLoading(true);
        getInventoryCached()
            .then((items) => setInventory(items))
            .catch(() => setInventory([]))
            .finally(() => setLoading(false));
    }, [visible, inventory.length]);

    // Local relevance matching (accent-insensitive, typo-tolerant).
    const results = useMemo(() => {
        if (normalizeText(query).length < 2) return [];
        const inStock = inventory.filter((p) => Number(p.cantidad_disponible || 0) > 0);
        return searchProducts(inStock, query).slice(0, 8);
    }, [query, inventory]);

    // Outside click + Escape closers.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        const onClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        window.addEventListener("mousedown", onClick);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("mousedown", onClick);
        };
    }, [open]);

    const goToResults = useCallback(() => {
        const term = query.trim();
        if (!term) return;
        navigate(`/tienda?q=${encodeURIComponent(term)}`);
        setOpen(false);
    }, [query, navigate]);

    const openProduct = (sku) => { navigate(`/producto/${sku}`); setOpen(false); };
    const displayName = (p) => p.nombre;

    if (!visible) return null;

    return (
        <div className="relative z-40 border-b border-[var(--tb-border)] bg-[var(--tb-bg)]/60 backdrop-blur">
            <div className="max-w-2xl mx-auto px-4 py-3">
                <div className="relative" ref={containerRef}>
                    <div className="flex items-center gap-2 bg-[var(--tb-bg-2)] border border-[var(--tb-border)] rounded-full px-4 py-2 transition focus-within:border-[var(--tb-gold)]/60 focus-within:bg-[var(--tb-bg)]">
                        <SearchIcon className="w-4 h-4 text-[var(--tb-gold)] flex-shrink-0" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                            onFocus={() => setOpen(true)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); goToResults(); } }}
                            placeholder={t("¿Qué buscas? Ej: zapatos para hombre, perfume dulce, T-0012…", "What are you looking for? E.g. men's shoes, sweet perfume, T-0012…")}
                            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--tb-muted)]"
                            data-testid="site-search-input"
                            aria-label={t("Buscar productos", "Search products")}
                        />
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-[var(--tb-muted)] flex-shrink-0" />}
                        {query && !loading && (
                            <button onClick={() => { setQuery(""); setOpen(false); }}
                                className="text-[var(--tb-muted)] hover:text-[var(--tb-gold)] flex-shrink-0"
                                aria-label={t("Limpiar búsqueda", "Clear search")}
                                data-testid="site-search-clear">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {open && query.trim().length >= 2 && (
                        <div className="absolute left-0 right-0 mt-2 bg-[var(--tb-bg)] border border-[var(--tb-border)] rounded-lg shadow-2xl overflow-hidden z-[100]"
                            data-testid="site-search-dropdown">
                            <div className="max-h-[60vh] overflow-y-auto">
                                {results.length === 0 && !loading ? (
                                    <div className="p-4 text-xs text-[var(--tb-muted)] text-center">
                                        {t("No encontramos productos que coincidan.", "No matching products found.")}
                                    </div>
                                ) : (
                                    <>
                                        {results.map((p) => {
                                            const sku = String(p.id || p.sku || "");
                                            const imgSrc = bridgeImage(p.foto, 96);
                                            return (
                                            <button
                                                key={sku}
                                                onClick={() => openProduct(sku)}
                                                className="w-full flex items-center gap-3 p-3 hover:bg-[var(--tb-border-gold)]/10 border-b border-[var(--tb-border)] text-left transition-colors"
                                                data-testid={`site-search-result-${sku}`}>
                                                {imgSrc ? (
                                                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                                                        <SmartImg src={imgSrc} className="w-full h-full object-cover" />
                                                    </div>
                                                ) : (
                                                    <div className="w-12 h-12 rounded bg-[var(--tb-bg-2)] flex-shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm truncate">{displayName(p)}</div>
                                                    <div className="text-[10px] text-[var(--tb-muted)] font-mono truncate">
                                                        {sku}{p.categoria ? ` · ${p.categoria}` : ""}
                                                    </div>
                                                </div>
                                                <div className="text-sm font-semibold text-[var(--tb-gold)] whitespace-nowrap">
                                                    {formatPrice(p.precio_venta)}
                                                </div>
                                            </button>
                                            );
                                        })}
                                        <button
                                            onClick={goToResults}
                                            className="w-full p-3 text-xs text-[var(--tb-gold)] hover:bg-[var(--tb-border-gold)]/10 font-medium tracking-wide uppercase"
                                            data-testid="site-search-see-all">
                                            {t("Ver todos los resultados", "See all results")} →
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
